import {
  faAngleDown,
  faFillDrip,
  faLongArrowAltDown,
  faLongArrowAltLeft,
  faLongArrowAltRight,
  faLongArrowAltUp,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, Popover } from 'antd';
import { clamp, isNaN } from 'lodash';
import React, { useEffect, useState } from 'react';
import { CompactPicker } from 'react-color';
import { images } from '../../../../../appCore';
import { BOTTOM, LEFT, RIGHT, TOP } from '../../../../../utils';
import { styles } from './styles';

const LINES = [
  { key: '5', title: 'Long Dash', value: [8, 4] },
  { key: '4', title: 'Dash-Dot', value: [4, 2, 1, 2] },
  { key: '3', title: 'Dashed', value: [2, 4] },
  { key: '2', title: 'Dotted', value: [1, 2] },
  { key: '1', title: 'Solid', value: [] },
];

const Inspector = ({ obj, initialState, updateHTMLData, axis = false, type }) => {
  const classes = styles();

  const [state, _setState] = useState(initialState);
  const setState = (obj) => _setState((_state) => ({ ..._state, ...obj }));

  const handleColorChange = ({ hex }) => {
    const data = JSON.parse(obj.htmlContent);
    if (axis) {
      data[axis] = {
        ...state,
        color: hex,
      };
    } else {
      data.grid = {
        ...state,
        color: hex,
      };
    }
    updateHTMLData(data);
  };

  const handleLineWidthChange = (e) => {
    const value = parseInt(e.target.value);
    const clampedValue = clamp(value, 0, 100);
    const finalValue = isNaN(clampedValue) ? 0 : clampedValue;
    setState({ lineWidth: finalValue });
    const data = JSON.parse(obj.htmlContent);
    if (axis) {
      data[axis] = {
        ...state,
        lineWidth: finalValue,
      };
    } else {
      data.grid = {
        ...state,
        lineWidth: finalValue,
      };
    }
    updateHTMLData(data, 10);
  };

  const handleDashTypeChange = (i) => {
    const dash = {
      title: LINES[i].title,
      value: LINES[i].value,
    };
    const data = JSON.parse(obj.htmlContent);
    data.grid = {
      ...state,
      dash,
    };
    updateHTMLData(data);
  };

  const lineItems = LINES.map((l, i) => ({
    key: l.key,
    label: <span onClick={() => handleDashTypeChange(i)}>{l.title}</span>,
  }));

  const handleDirectionChange = (t) => {
    const data = JSON.parse(obj.htmlContent);
    if ([TOP, BOTTOM].some((keyword) => t.includes(keyword))) {
      const xAxis = {
        ...state,
        position: t,
      };
      data.xAxis = xAxis;
    } else {
      const yAxis = {
        ...state,
        position: t,
      };
      data.yAxis = yAxis;
    }
    updateHTMLData(data);
  };

  const handleTicksChange = (v) => {
    const data = JSON.parse(obj.htmlContent);
    data[axis] = { ...data[axis], [v]: !state[v] };
    updateHTMLData(data);
  };

  useEffect(() => {
    if (initialState) {
      setState(initialState);
    }
  }, [initialState]);

  const directionOptions =
    axis === 'xAxis'
      ? [
          { position: BOTTOM, icon: faLongArrowAltDown },
          { position: TOP, icon: faLongArrowAltUp },
        ]
      : [
          { position: LEFT, icon: faLongArrowAltLeft },
          { position: RIGHT, icon: faLongArrowAltRight },
        ];

  const ticksOptions = [
    { className: `${classes.border} ${state.borderDisplay ? 'selected' : ''}`, label: 'Line', value: 'borderDisplay' },
    { className: `${classes.border} ${state.drawTicks ? 'selected' : ''}`, label: 'Ticks', value: 'drawTicks' },
    { className: `${classes.border} ${state.ticksDisplay ? 'selected' : ''}`, label: 'Labels', value: 'ticksDisplay' },
  ];

  const getTicksOptions = () => {
    if (axis !== 'rAxis') return ticksOptions;
    else {
      if (type.includes('polar')) return ticksOptions.slice(1, 2);
      else return ticksOptions.slice(1, 3);
    }
  };

  return (
    <div className={classes.inspectorContainer}>
      <div
        className={classes.row}
        style={{ justifyContent: `${axis ? 'flex-start' : 'space-between'}`, gap: `${axis ? '9px' : '0px'}` }}
      >
        <div className={classes.inspectorColorPicker}>
          <Popover
            zIndex={1071}
            content={<CompactPicker color={state.color} onChangeComplete={handleColorChange} />}
            trigger="click"
            onOpenChange={(v) => setState({ visible: v })}
          >
            <div className={`${classes.border} ${classes.inspectorColorPickerInner}`}>
              <FontAwesomeIcon icon={faFillDrip} />
              <div className={classes.colorPickerPreview} style={{ backgroundColor: state.color }}></div>
            </div>
          </Popover>
        </div>
        <div className={`${classes.border} ${classes.inspectorInput}`}>
          <input
            value={state.lineWidth}
            onChange={handleLineWidthChange}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          />
          <span>px</span>
        </div>
        {!axis ? (
          <div className={classes.inspectorDashType}>
            <Dropdown menu={{ items: lineItems }} trigger={['click']} overlayClassName={classes.dropdown}>
              <div className={`${classes.border} ${classes.inspectorDashTypeContainer}`}>
                <img src={images.LINE_TYPE} />
                <span>{state.dash.title}</span>
                <FontAwesomeIcon icon={faAngleDown} />
              </div>
            </Dropdown>
          </div>
        ) : (
          <>
            {axis === 'rAxis' && (
              <div className={classes.inspectorTicks}>
                {getTicksOptions().map((option, index) => (
                  <span key={index} className={option.className} onClick={() => handleTicksChange(option.value)}>
                    {option.label}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {axis && axis !== 'rAxis' && (
        <div className={classes.row} style={{ marginTop: '6px' }}>
          <div className={classes.inspectorDirection}>
            {directionOptions.map((direction, index) => (
              <div
                key={index}
                className={`${classes.border} ${classes.directionIcon} ${
                  state.position === direction.position ? 'selected' : ''
                }`}
                onClick={() => handleDirectionChange(direction.position)}
              >
                <FontAwesomeIcon icon={direction.icon} />
              </div>
            ))}
          </div>
          <div className={classes.inspectorTicks}>
            {getTicksOptions().map((option, index) => (
              <span key={index} className={option.className} onClick={() => handleTicksChange(option.value)}>
                {option.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Inspector;
